const DEFAULT_MAX_ROWS = 10_000

export interface ParsedCsv {
  headers: string[]
  rows: string[][]
  totalRows: number
}

/**
 * Split a single CSV line into fields, handling double-quoted fields that
 * may contain commas and escaped double-quotes ("").
 * Phase 1: does not handle quoted fields that span multiple physical lines.
 *
 * Notes:
 * - Quoted field content is `.trim()`-ed (interior-adjacent whitespace
 *   stripped) — a deliberate import-normalization choice.
 * - Text that immediately follows a closing quote (before the next comma or
 *   end-of-line) is absorbed into the same field rather than creating an
 *   extra column (e.g. `"x"yz` → the single field `xyz`).
 */
function splitLine(line: string): string[] {
  const fields: string[] = []
  let i = 0

  while (i < line.length) {
    if (line[i] === '"') {
      // Quoted field: skip opening quote, scan until closing quote
      i++
      let field = ''
      while (i < line.length) {
        if (line[i] === '"') {
          if (i + 1 < line.length && line[i + 1] === '"') {
            // Escaped double-quote ("")
            field += '"'
            i += 2
          } else {
            // Closing quote
            i++
            break
          }
        } else {
          field += line[i]
          i++
        }
      }
      // Bug 3 fix: absorb any trailing non-delimiter text into the same field
      // instead of letting it fall through and create a phantom extra column.
      while (i < line.length && line[i] !== ',') {
        field += line[i]
        i++
      }
      fields.push(field.trim())
      // Expect a comma delimiter or end of line
      if (i < line.length && line[i] === ',') i++
    } else {
      // Unquoted field: read until next comma or end of line
      const start = i
      while (i < line.length && line[i] !== ',') i++
      fields.push(line.slice(start, i).trim())
      if (i < line.length) i++ // skip comma
    }

    // Bug 2 fix: if we just consumed the last comma (i is now at end-of-line),
    // the trailing comma means an empty final field — add it regardless of
    // whether the preceding field was quoted or unquoted.
    if (i === line.length && line[line.length - 1] === ',') {
      fields.push('')
    }
  }

  return fields
}

/**
 * Parse CSV text into headers, rows (capped at `maxRows` for preview), and
 * the true total row count.
 *
 * - First non-empty line → headers (trimmed).
 * - Subsequent non-empty lines → rows; each cell is trimmed.
 * - Empty lines are ignored throughout.
 * - `totalRows` is always the TRUE count of data rows in the input, even when
 *   `rows` is capped by `maxRows`. This is intentional: the preview cap only
 *   limits what is returned in `rows`; the eventual commit step uses the full
 *   file, so callers need the accurate total for UI feedback.
 * - Default `maxRows`: 10 000.
 */
export function parseCsv(text: string, options?: { maxRows?: number }): ParsedCsv {
  const maxRows = options?.maxRows ?? DEFAULT_MAX_ROWS

  if (!text.trim()) {
    return { headers: [], rows: [], totalRows: 0 }
  }

  const lines = text.split(/\r\n|\r|\n/)
  let headers: string[] = []
  const rows: string[][] = []
  let totalRows = 0
  let headersSet = false

  for (const raw of lines) {
    if (!raw.trim()) continue // skip empty lines

    if (!headersSet) {
      headers = splitLine(raw)
      headersSet = true
      continue
    }

    totalRows++
    if (rows.length < maxRows) {
      rows.push(splitLine(raw))
    }
  }

  return { headers, rows, totalRows }
}

/**
 * Apply a header→target mapping to the parsed CSV rows.
 *
 * `mapping` maps a CSV header name to a target field name. Returns one object
 * per row in `parsed.rows`. Keys in the output are the TARGET field names.
 *
 * - Headers not present in `mapping` are omitted.
 * - Mapping entries with an empty-string target are omitted.
 * - Mapping keys not found in `parsed.headers` are skipped gracefully.
 * - Missing cells (ragged rows) → empty string; extra cells → ignored.
 * - Duplicate header names in `parsed.headers` resolve last-wins: the last
 *   column index for that header name is what `headerIndex` retains.
 */
export function applyMapping(
  parsed: ParsedCsv,
  mapping: Record<string, string>,
): Record<string, string>[] {
  // Build an index: headerIndex[csvHeader] = column index in parsed.headers
  const headerIndex = new Map<string, number>()
  parsed.headers.forEach((h, idx) => headerIndex.set(h, idx))

  // Pre-compute which mappings are usable: [colIndex, targetField]
  const usableMappings: Array<[number, string]> = []
  for (const [csvHeader, targetField] of Object.entries(mapping)) {
    if (!targetField) continue // skip empty-string targets
    const colIdx = headerIndex.get(csvHeader)
    if (colIdx === undefined) continue // header not found
    usableMappings.push([colIdx, targetField])
  }

  return parsed.rows.map((row) => {
    const obj: Record<string, string> = {}
    for (const [colIdx, targetField] of usableMappings) {
      obj[targetField] = row[colIdx] ?? ''
    }
    return obj
  })
}
